import { modelByArticleNumberQuery } from "@src/context/globalState/GlobalStateContext.gql";
import { type GQLBrandSelectionQuery, type GQLModelByArticleNumberQuery, type GQLModelByArticleNumberQueryVariables } from "@src/graphql.generated";
import { brandSelectionQuery } from "@src/pages/landingPage/components/brandSelection/BrandSelection.gql";
import { type Breadcrumb } from "@src/pages/visualSelectorPage/VisualSelectorPage";
import { apolloClient } from "@src/utils/apollo/apolloClient";
import * as React from "react";
import { type PropsWithChildren } from "react";

export interface GlobalStateContextValue {
    brandId?: string;
    breadcrumbs: Breadcrumb[];

    componentGroupId: string;
    contextArticleId?: string;

    modelId?: string;
    searchTerm: string;

    setBrandId: (value?: string) => void;
    setBreadcrumbs: (value: Breadcrumb[]) => void;

    setComponentGroupId: (value: string) => void;
    setContextArticleId: (value?: string) => void;

    setModelId: (value?: string) => void;
    setSearchTerm: (value: string) => void;

    setSupportedBrandIds: (value: string[]) => void;
    supportedBrandIds: string[];
}

const GlobalStateContext = React.createContext<GlobalStateContextValue>(undefined!);

const GlobalStateProvider: React.FunctionComponent<PropsWithChildren & { attributeBrands?: string[]; initialModelArticleNumber?: string }> = ({
    initialModelArticleNumber,
    attributeBrands,
    children,
}) => {
    const [brandId, setBrandId] = React.useState<string | undefined>(undefined);
    const [supportedBrandIds, setSupportedBrandIds] = React.useState<string[]>([]);
    const [breadcrumbs, setBreadcrumbs] = React.useState<Breadcrumb[]>([]);
    const [contextArticleId, setContextArticleId] = React.useState<string | undefined>(undefined);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [componentGroupId, setComponentGroupId] = React.useState<string>("");
    const [modelId, setModelId] = React.useState<string | undefined>(undefined);
    const [loading, setLoading] = React.useState<boolean>(initialModelArticleNumber !== undefined);

    // special logic to set modelId if initialModelArticleNumber is provided
    if (initialModelArticleNumber) {
        void apolloClient
            .query<GQLBrandSelectionQuery>({ query: brandSelectionQuery })
            .then(async (result) => {
                let brandIds: string[] = [];

                if (attributeBrands && attributeBrands.length > 0) {
                    brandIds = (result.data?.brands.nodes ?? [])
                        .filter((brand) => attributeBrands.includes(brand.shortName.toLowerCase()))
                        .map((brand) => brand.id);
                } else {
                    brandIds = (result.data?.brands.nodes ?? []).map((brand) => brand.id);
                }

                await apolloClient
                    .query<GQLModelByArticleNumberQuery, GQLModelByArticleNumberQueryVariables>({
                        query: modelByArticleNumberQuery,
                        variables: { articleNumber: initialModelArticleNumber, brandIds },
                    })
                    .then((result) => {
                        setModelId(result.data?.modelByArticleNumber?.id);
                    })
                    .finally(() => setLoading(false));
            })
            .finally(() => setLoading(false));
    }

    const contextValue = {
        brandId,
        setBrandId,
        supportedBrandIds,
        setSupportedBrandIds,
        breadcrumbs,
        setBreadcrumbs,
        searchTerm,
        setSearchTerm,
        contextArticleId,
        setContextArticleId,
        componentGroupId,
        setComponentGroupId,
        modelId,
        setModelId,
    };

    if (loading) {
        return null;
    }

    return <GlobalStateContext.Provider value={contextValue}>{children}</GlobalStateContext.Provider>;
};

export { GlobalStateContext, GlobalStateProvider };
